import { useState, useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { Info, X } from 'lucide-react'
import { DOCS_BY_ROUTE, SHORT_BLOCK_LABELS } from '@/docs/tier1Docs'

/**
 * Route-aware help control shown in the header. When the current route has a
 * documentation entry, it renders the page title + an ⓘ button; the button
 * opens a light drawer with the 5 short blocks and a link to the full guide.
 * Reads the shared docs registry (tier1Docs) — one content source.
 */
export default function HelpButton() {
  const { pathname } = useLocation()
  const entry = DOCS_BY_ROUTE[pathname]
  const [open, setOpen] = useState(false)

  // close the drawer whenever the route changes
  useEffect(() => { setOpen(false) }, [pathname])

  // Esc closes the drawer
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  if (!entry) return null

  return (
    <>
      <div className="flex items-center gap-1.5">
        <span className="text-base font-semibold">{entry.title}</span>
        <button
          onClick={() => setOpen(true)}
          aria-label={`Help for ${entry.title}`}
          title="What is this screen?"
          className="inline-flex items-center justify-center h-7 w-7 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
        >
          <Info className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={`${entry.title} help`}>
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-card border-l border-border shadow-xl flex flex-col animate-in slide-in-from-right">
            <div className="flex items-start justify-between px-5 py-4 border-b border-border">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{entry.section}</div>
                <h2 className="text-lg font-semibold truncate">{entry.title}</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close help"
                className="h-8 w-8 flex-none rounded-md hover:bg-muted flex items-center justify-center"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {SHORT_BLOCK_LABELS.map(({ key, label }) => (
                <div key={key}>
                  <div className="text-[11px] font-medium uppercase tracking-wider text-primary mb-1">{label}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{entry.short[key]}</p>
                </div>
              ))}
            </div>

            <div className="px-5 py-4 border-t border-border">
              <Link
                to={`/documentation#${entry.key}`}
                onClick={() => setOpen(false)}
                className="text-sm font-medium text-primary hover:underline"
              >
                Read full documentation →
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
